import { Button } from 'app/components/Button';
import { IOptionType } from 'app/components/Select/interface';
import {
  BinPhysicalInformation,
  MonthsWithoutInterestInformation,
  FormCardInformation,
  FormCustomer,
  FieldSelectEvaluated,
} from 'app/pages/frames/components';
import { getFormControlProps } from 'app/pages/frames/components/Form/FieldEvaluated/helpers';
import {
  formCardFieldNames,
  formControlsPropsCard,
  monthlyInstallmentsInitialState,
} from 'app/pages/frames/components/Form/FormCard/constants';
import { useMonthlyInstallments } from 'app/pages/frames/hooks/monthlyInstallments';
import { useBinPhysical } from 'app/pages/frames/hooks/useBinPhysical';
import { getBinFromCardNumber, monthlyInstallmentsInitialValue } from 'app/util/constants';
import { FormApi } from 'final-form';
import { useContext, useEffect, useState } from 'react';
import { CheckoutFrameContext } from 'app/context/CheckoutFrameContext';
import { getXprops } from 'app/helpers/getXprops';
import { isMonthlyInstallmentsSelectDisabled, parseMonthlyInstallmentsToOptionTypes } from './helpers';
import { IMonthlyInstallment } from './interfaces';
import { useTranslation } from '@conekta/cronos/i18n';
import { getErrorMessageForCustomer } from 'app/pages/frames/util/form';

export const BasicFormCard = ({ submitButtonText, loading, form, submitting, values, error, setErrorMessage }: any) => {
  const { checkoutRequest } = useContext(CheckoutFrameContext);
  const { monthlyInstallments: monthlyInstallmentsName } = formCardFieldNames;
  const { recurrent: isRecurrent, monthlyInstallmentsEnabled = false, openAmount } = checkoutRequest;
  const { t } = useTranslation();
  const { onEventListener } = getXprops() || {};

  const [binValue, setBinValue] = useState<string>('');
  const { binPhysicalProduct, validateBinPhysicalProduct } = useBinPhysical();
  const { doGet, error: monthlyInstallmentsInitialError } = useMonthlyInstallments();

  const [monthlyInstallments, setMonthlyInstallments] = useState<IMonthlyInstallment[]>([]);
  const [monthlyInstallmentsOptions, setMonthlyInstallmentsOptions] = useState<IOptionType[]>([
    ...monthlyInstallmentsInitialState(t),
  ]);

  const validateBinCard = async (cardForm: FormApi<any>): Promise<void> => {
    const { change, getFieldState } = cardForm;
    const cardNumberState = getFieldState(formCardFieldNames.cardNumber);
    const cardNumberHasAnError: string | undefined = cardNumberState?.error;
    const bin = getBinFromCardNumber(cardNumberState?.value);

    validateBinPhysicalProduct(cardForm);

    if (cardNumberHasAnError) {
      setMonthlyInstallmentsOptions([...monthlyInstallmentsInitialState(t)]);
      change('monthlyInstallments', monthlyInstallmentsInitialValue);
    } else {
      if (binValue !== bin) {
        setBinValue(bin);
        const monthlyInstalmentsResponse = await doGet(checkoutRequest.id, bin);

        setMonthlyInstallments(monthlyInstalmentsResponse);
        setMonthlyInstallmentsOptions([
          ...monthlyInstallmentsInitialState(t),
          ...parseMonthlyInstallmentsToOptionTypes(monthlyInstalmentsResponse, t),
        ]);
      }
    }
  };
  const onMonthlyInstallmentsFieldChange = (value: number) => {
    const monthlyInstallmentSelected: IMonthlyInstallment | undefined = monthlyInstallments.find(
      ({ monthlyInstallments: monthlyInstallment }: IMonthlyInstallment) => monthlyInstallment === value,
    );
    onEventListener &&
      onEventListener({
        name: 'monthlyInstallmentSelected',
        value: monthlyInstallmentSelected,
      });
  };

  useEffect(() => {
    if (!error && monthlyInstallmentsInitialError) {
      setErrorMessage(getErrorMessageForCustomer(monthlyInstallmentsInitialError));
    }
  }, [error, monthlyInstallmentsInitialError]);

  return (
    <>
      {(isRecurrent || openAmount) && <FormCustomer />}

      <FormCardInformation form={form} validateCardNumber={validateBinCard} />

      {monthlyInstallmentsEnabled && (
        <div className="fields__monthlyInstallments">
          <FieldSelectEvaluated
            disabled={isMonthlyInstallmentsSelectDisabled(monthlyInstallmentsOptions)}
            options={monthlyInstallmentsOptions}
            {...getFormControlProps(monthlyInstallmentsName, formControlsPropsCard, t)}
            {...{ values }}
            onChange={onMonthlyInstallmentsFieldChange}
            tooltipHelper={<MonthsWithoutInterestInformation />}
          />
        </div>
      )}
      <BinPhysicalInformation show={binPhysicalProduct} />
      <Button
        className="FormCard__btn-submit"
        disabled={submitting || loading}
        isLoading={submitting || loading}
        type="submit"
      >
        {submitButtonText}
      </Button>
    </>
  );
};
